import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Users, Briefcase, TrendingUp, Plus, Inbox, Clock, FileText, ChevronDown, X, Lock, Video, MapPin, XCircle, Check } from 'lucide-react';

const SUBSCRIPTION_TIER = 'paid'; // Demo employer is paid subscriber

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ matches: 0, roles: 0, interviews: 0 });
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null); // {appId, action}
  const [rejectionTemplate, setRejectionTemplate] = useState('not_hiring');
  const [rejectionTemplates, setRejectionTemplates] = useState([]);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [f2fMessage, setF2fMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, appsRes, templatesRes] = await Promise.all([
          axios.get(`${API_BASE}/roles`, { params: { employer_id: user.id } }).catch(() => ({ data: [] })),
          axios.get(`${API_BASE}/applications/employer/${user.id}`).catch(() => ({ data: { applications: [] } })),
          axios.get(`${API_BASE}/rejection-templates`).catch(() => ({ data: { templates: [] } })),
        ]);
        setStats({ roles: (rolesRes.data || []).length, matches: 0, interviews: 0 });
        const sortedApps = (appsRes.data.applications || []).sort((a, b) => (b.suitability_score || 0) - (a.suitability_score || 0));
        setApplications(sortedApps);
        setRejectionTemplates(templatesRes.data.templates || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.id]);

  const handleAction = async (appId, action) => {
    setActionLoading(true);
    try {
      const payload = { action, employer_name: user.name || 'STS Group' };
      if (action === 'reject') payload.template_id = rejectionTemplate;
      if (action === 'invite-online') { payload.interview_date = interviewDate; payload.interview_time = interviewTime; }
      if (action === 'invite-face-to-face') { payload.message = f2fMessage; }

      await axios.post(`${API_BASE}/employer/action/${appId}`, payload);
      toast.success(action === 'reject' ? 'Candidate notified' : 'Interview invitation sent! (Email notification mocked)');

      // Refresh applications
      const res = await axios.get(`${API_BASE}/applications/employer/${user.id}`);
      setApplications((res.data.applications || []).sort((a, b) => (b.suitability_score || 0) - (a.suitability_score || 0)));
      setActiveAction(null);
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0D9488]" /></div>;

  const pending = applications.filter(a => a.status === 'submitted');
  const actioned = applications.filter(a => a.status !== 'submitted');

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Employer Dashboard</h1>
            <p className="text-base text-slate-500">Welcome back, {user.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">{SUBSCRIPTION_TIER === 'paid' ? 'Premium Subscriber' : 'Free Tier'}</span>
            <Button onClick={() => navigate('/employer/post-role')} className="bg-[#0D9488] hover:bg-[#0B7C72] text-white rounded-full" data-testid="post-role-btn">
              <Plus className="w-4 h-4 mr-2" /> Post Role
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <Inbox className="w-8 h-8 text-[#0D9488] mb-2" />
            <div className="text-3xl font-bold text-slate-900">{applications.length}</div>
            <div className="text-sm text-slate-500">Applications</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <Clock className="w-8 h-8 text-orange-500 mb-2" />
            <div className="text-3xl font-bold text-slate-900">{pending.length}</div>
            <div className="text-sm text-slate-500">Pending Review</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <Briefcase className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-3xl font-bold text-slate-900">{actioned.length}</div>
            <div className="text-sm text-slate-500">Actioned</div>
          </div>
        </div>

        {/* Applications Inbox */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Inbox className="w-6 h-6 text-[#0D9488]" /> Candidate Applications
          </h2>
          <p className="text-sm text-slate-500 mb-4">Ranked by suitability score. Higher scores indicate a stronger match.</p>

          {applications.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No applications received yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map(app => {
                const score = app.suitability_score || 0;
                const scoreColor = score >= 80 ? '#34C759' : score >= 60 ? '#0071e3' : score >= 40 ? '#FF9500' : '#FF3B30';
                const isActive = activeAction?.appId === app.application_id;

                return (
                  <div key={app.application_id} className={`rounded-xl border transition-all ${app.status === 'submitted' ? 'border-slate-200' : app.status === 'rejected' ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}`}
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Suitability Score */}
                      <div className="w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: `${scoreColor}15` }}>
                        <span className="text-lg font-bold" style={{ color: scoreColor }}>{score}</span>
                        <span className="text-[9px] text-slate-400">/ 100</span>
                      </div>

                      {/* Candidate Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-slate-900">{app.candidate_name}</h3>
                        <p className="text-xs text-slate-500">
                          Ref: {app.candidate_ref}
                          {app.role_title && <> &middot; Applied for: <span className="font-medium text-slate-700">{app.role_title}</span></>}
                          {app.type === 'proactive' && <> &middot; <span className="text-blue-600">Proactive</span></>}
                          {app.type === 'direct' && <> &middot; <span className="text-green-600">Direct</span></>}
                        </p>
                        <p className="text-xs text-slate-400">{new Date(app.created_at).toLocaleDateString('en-IE')}</p>
                      </div>

                      {/* Status / Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {app.status === 'submitted' ? (
                          <>
                            {SUBSCRIPTION_TIER === 'paid' ? (
                              <>
                                <Button onClick={() => setActiveAction(isActive ? null : { appId: app.application_id, action: 'reject' })} variant="outline" size="sm" className="rounded-full border-red-300 text-red-600 hover:bg-red-50 text-xs h-8 px-3" data-testid={`reject-${app.application_id}`}>
                                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                </Button>
                                <Button onClick={() => setActiveAction(isActive && activeAction.action === 'invite-online' ? null : { appId: app.application_id, action: 'invite-online' })} variant="outline" size="sm" className="rounded-full border-blue-300 text-blue-600 hover:bg-blue-50 text-xs h-8 px-3" data-testid={`invite-online-${app.application_id}`}>
                                  <Video className="w-3.5 h-3.5 mr-1" /> Online
                                </Button>
                                <Button onClick={() => setActiveAction(isActive && activeAction.action === 'invite-face-to-face' ? null : { appId: app.application_id, action: 'invite-face-to-face' })} variant="outline" size="sm" className="rounded-full border-emerald-300 text-emerald-600 hover:bg-emerald-50 text-xs h-8 px-3" data-testid={`invite-f2f-${app.application_id}`}>
                                  <MapPin className="w-3.5 h-3.5 mr-1" /> In-Person
                                </Button>
                              </>
                            ) : (
                              <div className="flex items-center gap-2 text-sm text-slate-400">
                                <Lock className="w-4 h-4" /> <span>Upgrade to take action</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${app.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {app.status === 'rejected' ? 'Rejected' : app.status === 'interview_scheduled' ? `Interview (${app.interview_type})` : app.status}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Panel */}
                    {isActive && (
                      <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                        {activeAction.action === 'reject' && (
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Select rejection reason:</p>
                            <div className="space-y-2 mb-3">
                              {rejectionTemplates.map(t => (
                                <button key={t.id} onClick={() => setRejectionTemplate(t.id)}
                                  className={`w-full text-left p-3 rounded-lg text-sm transition-all ${rejectionTemplate === t.id ? 'bg-red-50 border-2 border-red-300' : 'bg-slate-50 border border-slate-200 hover:border-slate-300'}`}
                                  data-testid={`template-${t.id}`}
                                >
                                  <span className="font-medium">{t.label}</span>
                                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.text}</p>
                                </button>
                              ))}
                            </div>
                            <Button onClick={() => handleAction(app.application_id, 'reject')} disabled={actionLoading} className="bg-red-600 hover:bg-red-700 text-white rounded-full text-sm" data-testid="confirm-reject">
                              {actionLoading ? 'Sending...' : 'Send Rejection & Notify Candidate'}
                            </Button>
                          </div>
                        )}
                        {activeAction.action === 'invite-online' && (
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Schedule online interview:</p>
                            <div className="flex gap-3 mb-3">
                              <input type="date" value={interviewDate} onChange={e => setInterviewDate(e.target.value)} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="interview-date" />
                              <input type="time" value={interviewTime} onChange={e => setInterviewTime(e.target.value)} className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="interview-time" />
                            </div>
                            <Button onClick={() => handleAction(app.application_id, 'invite-online')} disabled={actionLoading || !interviewDate || !interviewTime} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm" data-testid="confirm-invite-online">
                              {actionLoading ? 'Sending...' : 'Send Online Interview Invite'}
                            </Button>
                          </div>
                        )}
                        {activeAction.action === 'invite-face-to-face' && (
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Message to candidate:</p>
                            <textarea value={f2fMessage} onChange={e => setF2fMessage(e.target.value)} rows={3} placeholder="e.g., We'd love to meet you at our office at 10 Railway Square, Waterford..." className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none mb-3" data-testid="f2f-message" />
                            <Button onClick={() => handleAction(app.application_id, 'invite-face-to-face')} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-sm" data-testid="confirm-invite-f2f">
                              {actionLoading ? 'Sending...' : 'Send Face-to-Face Interview Invite'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
